import type { WsCloseInfo } from './ws-client';

export type ReconnectVariant =
  | 'reconnecting'
  | 'server-update'
  | 'server-unavailable'
  | 'connection-interrupted';

export type ControlProbeResult = 'ok' | 'failed' | 'timeout' | 'mismatched';

export interface ReconnectClassifierInput {
  readonly lastCloseInfo: WsCloseInfo | null;
  readonly failureCount: number;
  readonly elapsedMs: number;
  readonly online: boolean | null;
  readonly controlProbeResult?: ControlProbeResult | null;
  readonly sameOriginWs: boolean;
}

export const SERVER_RESTART_CLOSE_CODE = 1012;
export const SERVER_RESTART_CLOSE_REASON = 'server_restart';
export const SESSION_RESTORED_ELSEWHERE_CLOSE_CODE = 4000;
export const SESSION_RESTORED_ELSEWHERE_REASON = 'session_restored_elsewhere';
export const SERVER_UNAVAILABLE_FAILURE_THRESHOLD = 3;
export const SERVER_UNAVAILABLE_ELAPSED_MS = 5_000;

export function isSessionRestoredElsewhereClose(closeInfo: WsCloseInfo | null): boolean {
  return (
    closeInfo?.code === SESSION_RESTORED_ELSEWHERE_CLOSE_CODE &&
    closeInfo.reason === SESSION_RESTORED_ELSEWHERE_REASON
  );
}

export function isGracefulServerRestartClose(closeInfo: WsCloseInfo | null): boolean {
  return (
    closeInfo?.code === SERVER_RESTART_CLOSE_CODE &&
    closeInfo.reason === SERVER_RESTART_CLOSE_REASON
  );
}

export function isSameOriginGameWsUrl(
  wsUrl: string,
  location: Pick<Location, 'protocol' | 'host' | 'href'>,
): boolean {
  let parsed: URL;
  try {
    parsed = new URL(wsUrl, location.href);
  } catch {
    return false;
  }

  const expectedProtocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const hasExplicitProtocol = /^[a-z][a-z\d+\-.]*:/i.test(wsUrl);
  const protocolMatches =
    parsed.protocol === expectedProtocol ||
    (!hasExplicitProtocol && parsed.protocol === location.protocol);
  return (
    protocolMatches &&
    parsed.host === location.host &&
    (parsed.pathname === '/ws-game' || parsed.pathname.startsWith('/ws-game/'))
  );
}

export function classifyReconnect(input: ReconnectClassifierInput): ReconnectVariant | null {
  if (isSessionRestoredElsewhereClose(input.lastCloseInfo)) return null;
  if (isGracefulServerRestartClose(input.lastCloseInfo)) return 'server-update';
  if (input.online === false) return 'connection-interrupted';
  if (input.controlProbeResult === 'failed' || input.controlProbeResult === 'timeout') {
    return 'connection-interrupted';
  }
  const hasRepeatedFailures =
    input.failureCount >= SERVER_UNAVAILABLE_FAILURE_THRESHOLD ||
    input.elapsedMs >= SERVER_UNAVAILABLE_ELAPSED_MS;
  if (input.sameOriginWs && hasRepeatedFailures) {
    return 'server-unavailable';
  }

  return 'reconnecting';
}
